export interface Meta { current_page?: number; last_page?: number; total?: number; }
export interface Envelope<T> { status: string; message: string; data: T; meta?: Meta; }

export interface AuthUser { id: string; email: string; full_name: string; user_role: string; }

export interface DashboardKpis {
  total_companies: number;
  active_subscriptions: number;
  mrr: number;
  new_companies_this_month: number;
  pending_invoices_count: number;
}

export interface AdminCompany {
  id: string; company_name: string; created_at: string;
  employee_count: number; subscription_status: string;
  valid_until: string | null; is_suspended: boolean;
}

export interface CompanyBranch { id: string; name: string | null; }

export interface AdminCompanyDetail {
  id: string; company_name: string; company_code: string | null; business_type: string | null;
  company_phone: string | null; address: string | null; created_at: string;
  employee_count: number; subscription_status: string; is_suspended: boolean;
  subscription: AdminCompanySubscription | null;
  branches: CompanyBranch[];
}

/** Raw subscription row as returned inside AdminCompanyDetail. */
export interface AdminCompanySubscription {
  status: string;
  valid_until: string | null;
  price_per_user: number;
  user_count: number;
  is_trial: boolean;
  station_quota: number;
  addon_user_quota: number;
}

export type BusinessType = 'tech' | 'retail' | 'service' | 'fnb' | 'other';

export const BUSINESS_TYPE_OPTIONS: { value: BusinessType; label: string }[] = [
  { value: 'tech', label: 'Teknologi' },
  { value: 'retail', label: 'Retail' },
  { value: 'service', label: 'Jasa' },
  { value: 'fnb', label: 'F&B' },
  { value: 'other', label: 'Lainnya' },
];

export type TrialDays = 7 | 14 | 30 | 60 | 90;

export const TRIAL_DAY_OPTIONS: { value: TrialDays; label: string }[] = [
  { value: 7, label: '7 hari' },
  { value: 14, label: '14 hari' },
  { value: 30, label: '1 bulan' },
  { value: 60, label: '2 bulan' },
  { value: 90, label: '3 bulan' },
];

/** Body of POST admin/companies. */
export interface CreateCompanyPayload {
  company_name: string;
  business_type: BusinessType;
  other_business_type?: string;
  indonesia_address_id: number;
  address?: string;
  admin_name: string;
  admin_email: string;
  admin_phone: string;
  password?: string;
  trial_days: TrialDays;
  station_quota: number;
  addon_user_quota: number;
}

/** Body of PUT admin/companies/{id}/trial. */
export interface UpdateTrialPayload {
  station_quota: number;
  addon_user_quota: number;
  trial_days: TrialDays;
}

export type AdminCompanyCreateResult = AdminCompanyDetail & { email_sent: boolean };

/** One row of GET indonesia-addresses/search. */
export interface IndonesiaAddress {
  id: number;
  kelurahan: string;
  kecamatan: string;
  kabupaten: string;
  provinsi: string;
  kodepos: string;
}

export const addressLabel = (a: IndonesiaAddress) => `${a.kelurahan}, ${a.kecamatan}, ${a.kabupaten}, ${a.provinsi} ${a.kodepos}`;

export interface AdminUser {
  id: string; full_name: string; email: string;
  company_id: string; company_name: string | null;
  user_role: string; job_title: string | null; is_active: boolean;
}

export type InvoiceType = 'renewal' | 'quota_addition';
export type InvoiceStatus = 'pending' | 'paid' | 'cancelled';

export interface Invoice {
  id: string; company_id: string; company_name: string | null;
  type: InvoiceType;
  months: number; user_count: number;
  station_quota: number; addon_user_quota: number;
  station_price: number; addon_user_price: number;
  discount_percent: number;
  prorate_days: number | null; prorate_amount: number | null;
  amount: number; status: InvoiceStatus;
  period_start: string | null; period_end: string | null; paid_at: string | null;
  cancelled_at: string | null; cancelled_by: string | null;
  created_at: string;
}

/** Map of duration-in-months (as a string key, e.g. "3") to discount percent (0-100). */
export type DurationDiscounts = Record<string, number>;

export interface SubscriptionSetting {
  station_price: number;
  /** Employees included per station. Fixed platform-wide — never editable from here (see note in Subscriptions page). */
  users_per_station: number;
  addon_user_price: number;
  duration_discounts: DurationDiscounts;
  min_subscription_months: number;
  trial_days: number;
  trial_user_limit: number;
  admin_whatsapp: string;
  /** Legacy fields kept for old clients; no longer gate anything. */
  price_per_user: number;
  free_user_count: number;
  /** Destination bank account shown to companies on a pending invoice. All nullable — empty until filled in here. */
  bank_name: string | null;
  bank_account_number: string | null;
  bank_account_holder: string | null;
}

/** Payload accepted by PUT admin/subscriptions/settings — everything except the locked users_per_station. */
export type SubscriptionSettingUpdate = Omit<SubscriptionSetting, 'users_per_station'>;

export interface AdminStationRow {
  id: string;
  name: string;
  address: string | null;
  location_lat: string | null;
  location_long: string | null;
  distance_limit: number;
  clock_in_time: string | null;
  clock_out_time: string | null;
  timezone: string | null;
  uses_shift: boolean;
  shift_count: number;
  employee_count: number;
  attendance_total: number;
  attendance_30d: number;
  last_attendance_at: string | null;
}

export interface AdminEmployeeRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  job_title: string | null;
  user_role: string | null;
  company_role: string | null;
  station_name: string | null;
  is_active: boolean;
  attendance_30d: number;
  last_attendance_at: string | null;
}

export interface AdminCompanyOverview {
  stats: {
    active_employees: number;
    inactive_employees: number;
    station_count: number;
    attendance_30d: number;
    station_quota: number;
    addon_user_quota: number;
    free_user_count: number;
    user_quota: number;
    occupied_seats: number;
    registered_at: string | null;
    onboarding_completed_at: string | null;
    subscription_status: string;
    is_trial: boolean;
    valid_until: string | null;
    days_remaining: number;
  };
  activity: {
    daily: { date: string; count: number }[];
    status_30d: Record<string, number>;
  };
  invoices: {
    id: string;
    type: string;
    months: number | null;
    amount: number;
    status: string;
    created_at: string;
    paid_at: string | null;
  }[];
  adoption: {
    tasks_total: number;
    tasks_30d: number;
    video_log_groups: number;
    video_logs_30d: number;
    payslips_total: number;
    last_payslip_at: string | null;
    benefits_active: number;
    corrections_30d: number;
    overtime_30d: number;
    vacations_30d: number;
  };
  settings: {
    timezone: string;
    workdays: number[];
    holiday_count: number;
    roles: { name: string; member_count: number }[];
  };
  pending_invitations: {
    full_name: string | null;
    phone: string | null;
    created_at: string;
  }[];
  suspensions: {
    is_suspended: boolean;
    suspended_at: string | null;
    reason: string | null;
    suspended_by_name: string | null;
  }[];
}
